/**
 * API client.
 *
 * The access token is kept in memory only — never in localStorage, where any
 * script on the page could read it. The refresh token lives in an httpOnly
 * cookie the browser sends automatically, so a reload can silently restore a
 * session without JavaScript ever holding it.
 */
import type { LoginResult } from './types';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

let accessToken: string | null = null;
/** De-duplicates concurrent refreshes so a burst of 401s triggers one call. */
let refreshInFlight: Promise<boolean> | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when a refresh fails, so the app can drop to the sign-in screen. */
export function setUnauthenticatedHandler(fn: (() => void) | null): void {
  onUnauthenticated = fn;
}

export class ApiError extends Error {
  readonly status: number;
  /** The parsed JSON body, which carries the structured detail we surface. */
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** The `code` a structured error carries, e.g. account_locked. */
  get code(): string | undefined {
    const b = this.body as { code?: string } | null;
    return b?.code;
  }

  /** Gate-check failures returned by POST /applications/:id/submit. */
  get failures(): Array<{ id: string; label: string; detail?: string }> {
    const b = this.body as
      | { failures?: Array<{ id: string; label: string; detail?: string }> }
      | null;
    return b?.failures ?? [];
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Set for the auth endpoints, which must not recurse into a refresh. */
  skipRefresh?: boolean;
  signal?: AbortSignal;
  raw?: boolean;
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function messageOf(body: unknown, fallback: string): string {
  if (typeof body === 'string' && body) return body;
  const b = body as { message?: string | string[] } | null;
  if (!b?.message) return fallback;
  return Array.isArray(b.message) ? b.message.join(' ') : b.message;
}

async function attempt(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  return fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    // Sends and receives the httpOnly refresh cookie.
    credentials: 'include',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
}

/**
 * Exchanges the refresh cookie for a new access token. Concurrent callers all
 * await the same in-flight request.
 */
export function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: '{}',
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken?: string };
      if (!data.accessToken) return false;
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function request<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  let res = await attempt(path, opts);

  // One silent retry after a refresh — the access token is short-lived by
  // design, so an expiry mid-session is routine rather than exceptional.
  if (res.status === 401 && !opts.skipRefresh) {
    const ok = await refreshSession();
    if (ok) {
      res = await attempt(path, opts);
    } else {
      accessToken = null;
      onUnauthenticated?.();
    }
  }

  if (!res.ok) {
    const body = await parse(res);
    throw new ApiError(res.status, messageOf(body, res.statusText), body);
  }

  if (opts.raw) return (await res.text()) as unknown as T;
  if (res.status === 204) return undefined as T;
  return (await parse(res)) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  text: (path: string) => request<string>(path, { raw: true }),
};

// --- auth endpoints (these must not trigger a refresh) ---------------------

export function login(
  email: string,
  password: string,
  rememberDevice: boolean,
): Promise<LoginResult> {
  return request<LoginResult>('/auth/login', {
    method: 'POST',
    body: { email, password, rememberDevice },
    skipRefresh: true,
  });
}

export function verifyMfa(
  challengeId: string,
  code: string,
  rememberDevice: boolean,
): Promise<LoginResult> {
  return request<LoginResult>('/auth/mfa/verify', {
    method: 'POST',
    body: { challengeId, code, rememberDevice },
    skipRefresh: true,
  });
}

/** Switches the half-finished sign-in to another factor, or resends the code. */
export function resendMfa(
  challengeId: string,
  method?: string,
): Promise<LoginResult> {
  return request<LoginResult>('/auth/mfa/resend', {
    method: 'POST',
    body: { challengeId, ...(method ? { method } : {}) },
    skipRefresh: true,
  });
}

export function resetPassword(token: string, password: string): Promise<void> {
  return request<void>('/auth/password/reset', {
    method: 'POST',
    body: { token, password },
    skipRefresh: true,
  });
}

export function inviteDetails(token: string): Promise<{
  valid: boolean;
  email?: string;
  organisation?: string;
  role?: string;
  expiresAt?: string;
}> {
  return request('/auth/invite/' + encodeURIComponent(token), {
    skipRefresh: true,
  });
}

export function requestPasswordReset(email: string): Promise<{ message: string }> {
  return request('/auth/password/reset-request', {
    method: 'POST',
    body: { email },
    skipRefresh: true,
  });
}

export function acceptInvite(payload: {
  token: string;
  name: string;
  password: string;
  mobile?: string;
}): Promise<LoginResult> {
  return request<LoginResult>('/auth/invite/accept', {
    method: 'POST',
    body: payload,
    skipRefresh: true,
  });
}

export function logout(): Promise<void> {
  return request<void>('/auth/logout', {
    method: 'POST',
    skipRefresh: true,
  });
}
